






































import { Component, Vue } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import ConversionsList from '@/componentsV2/organisms/ConversionsList/ConversionsList.vue';
import Trigger from '@/componentsV2/molecules/Trigger/Trigger.vue';
import { CustomSelectOption } from '@/components/CustomSelect/CustomSelectModel';

import {
  EInitiativesDictionary,
  InitiativeLabelDictionary
} from '@/models/initiatives';
import { IConversion, IInitiativeFilter } from '@/models/performance';

import PerformanceConversionsService from '@/api/PerformanceConversionsService';

import PaginationUtils, {
  EPaginationSortDir,
  IPagination,
  IPaginationTableHeader
} from '@/utils/PaginationUtils';
import { applyPerformanceDataFilter } from '@/views/Performance/flag-helpers';
import flagKeys from '@/constants/flagKeys';
import DateUtils from '@/utils/DateUtils';
import { IPeriod } from '@/models/period';
import PerformanceUtils from '@/utils/PerformanceUtils';
import { EBusinessReportType } from '@/models/business';
import { appIsActive } from '@/router/validators/utils';

@Component({
  components: {
    SectionData,
    ConversionsList,
    Trigger
  }
})
export default class PerformanceRedemptionActivity extends Vue {
  performanceConversionsService = new PerformanceConversionsService();

  initiatives: IInitiativeFilter[] = this.prevRouteIsCustomerDetail
    ? this.cachedInitiatives
    : [];
  pagination: IPagination = this.prevRouteIsCustomerDetail
    ? this.cachedPagination
    : PaginationUtils.defaultPagination();
  filters: Record<string, any> = this.prevRouteIsCustomerDetail
    ? this.cachedFilters
    : {};
  period: IPeriod = this.initialPeriod;

  conversions: IConversion[] = this.prevRouteIsCustomerDetail
    ? this.cachedList
    : [];
  headers: Record<string, IPaginationTableHeader> = {
    'customer.name': {
      label: 'Member',
      sortable: true
    },
    'coupon.code': {
      label: 'Generated codes',
      sortable: true
    },
    'coupon.status': {
      label: 'Status',
      sortable: true
    },
    ...(this.showTotalConverted
      ? {
          ['coupon.coinsValue']: {
            label: 'Total converted',
            sortable: true
          }
        }
      : {}),
    'coupon.initiative': {
      label: 'Initiative',
      sortable: true
    },
    'coupon.createdAt': {
      label: 'Conversion date',
      sortable: true,
      sortDir: EPaginationSortDir.DESC
    }
  };
  search = {
    placeholder: 'Search by name or code',
    term:
      this.prevRouteIsCustomerDetail && this.cachedFilters.search
        ? this.cachedFilters.search
        : ''
  };

  get tPrefix() {
    return 'performance.redemptions.list';
  }

  get description() {
    const type = appIsActive() ? 'default' : 'dontHaveAccessOffers';
    return this.$t(`${this.tPrefix}.description.${type}`);
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'];
  }

  get showTotalConverted() {
    return this.businessInfo.reportType !== EBusinessReportType.OFFER;
  }

  get filterGATracking() {
    return {
      eventCategory: 'Conversions List - Tag Filter',
      eventAction: 'click'
    };
  }

  get configSection() {
    return {
      descriptionMaxWidth: '684px',
      padding: 0,
      titleFontSize: '20px',
      datepickerPosition: 'rightOfTheSearch'
    };
  }

  get gaTracking() {
    return {
      search: {
        eventCategory: 'Conversions List - Search',
        eventAction: 'change'
      },
      select: {
        eventCategory: 'Conversions List - Select',
        eventAction: 'change'
      }
    };
  }

  get initialPeriod() {
    const { from = null, to = null } = this.cachedFilters;

    if (!this.prevRouteIsCustomerDetail || !from || !to) {
      return PerformanceUtils.defaultPeriod();
    }

    return {
      start: from,
      end: to
    };
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get businessReferralIsActivated() {
    return this.flags.includes(flagKeys.businessReferral);
  }

  get customerImportIsActivated() {
    return this.flags.includes(flagKeys.customerImport);
  }

  get cachedList() {
    return this.$store.getters['performance/getConversionsState'].list;
  }

  get cachedInitiatives() {
    return this.$store.getters['performance/getConversionsState'].initiatives;
  }

  get cachedPagination() {
    return this.$store.getters['performance/getConversionsState'].config
      .pagination;
  }

  get prevRouteIsCustomerDetail() {
    return (
      window.previousRouteName &&
      window.previousRouteName === 'performance-customer-center-detail'
    );
  }

  get cachedFilters() {
    return this.$store.getters['performance/getConversionsState'].config
      .filters;
  }

  mounted() {
    const { status = '' } = this.cachedFilters;

    const finalStatus = this.prevRouteIsCustomerDetail && status ? status : '';

    if (finalStatus) {
      this.filters = Object.assign({}, this.filters, { status: finalStatus });
    }
  }

  handleItemClick(rowData: Record<string, any>) {
    const containerScrollElement = document.querySelector(
      '.container-scrollable'
    );

    this.$store.dispatch('performance/SET_CONFIG', {
      context: 'conversions',
      config: {
        pagination: this.pagination,
        filters: this.filters,
        ...(containerScrollElement && {
          scrollYPos: containerScrollElement.scrollTop
        })
      }
    });
    this.$store.dispatch('performance/SET_INITIATIVES', {
      context: 'conversions',
      initiatives: this.initiatives
    });
    this.$store.dispatch('performance/SET_LIST_DATA', {
      context: 'conversions',
      list: this.conversions
    });
  }

  handlePeriodChange(period: IPeriod) {
    const newPeriod = PerformanceUtils.normalizedPeriod(period);

    this.period = newPeriod;
    this.filters = {
      ...this.filters,
      from: newPeriod.start,
      to: newPeriod.end
    };

    this.loadConversions({ clearList: true });
  }

  formattedConversions(conversions: IConversion[]) {
    return conversions.map(item => ({
      ...item,
      coupon: {
        ...item.coupon,
        initiativeLabel:
          item.coupon.initiative == EInitiativesDictionary.FREE_COINS
            ? InitiativeLabelDictionary[EInitiativesDictionary.WELCOME_COINS]
            : InitiativeLabelDictionary[
                item.coupon.initiative as EInitiativesDictionary
              ],
        createdAt: item.coupon.createdAt
          ? DateUtils.formattedDate({
              date: item.coupon.createdAt,
              applyTimezone: true
            })
          : '--',
        coinsValue: `${Number(item.coupon.coinsValue)} ${this.$tc(
          `common.pluralize.coin`,
          Number(item.coupon.coinsValue) || 1
        )}`
      }
    }));
  }

  async loadConversions({ clearList = false } = {}) {
    if (clearList) {
      this.pagination = PaginationUtils.defaultPagination();
    }

    if (!this.pagination.mustCallApi) return;

    let endpointPath = '/admin/coupon';

    if (this.pagination.next) {
      endpointPath = this.pagination.next;
    }

    this.$showLoaderWithLocker();

    const response = await this.performanceConversionsService.getConversions(
      endpointPath,
      this.filters
    );

    this.$hideLoader();

    if (response.status !== 200) return;

    const { items, links, filters } = response.data;

    if (filters) {
      this.initiatives = filters.initiatives
        .filter(
          applyPerformanceDataFilter(
            this.businessReferralIsActivated,
            'type',
            flagKeys.referralProgram
          )
        )
        .filter(
          applyPerformanceDataFilter(
            this.customerImportIsActivated,
            'type',
            flagKeys.customerImport
          )
        );
    }

    Object.assign(this.pagination, {
      next: links.next,
      mustCallApi: !!links.next
    });

    if (clearList) {
      this.conversions = this.formattedConversions(items);

      return;
    }

    this.conversions = [
      ...this.conversions,
      ...this.formattedConversions(items)
    ];

    PaginationUtils.makeNewRequest(
      {
        triggerClass: '.infinite-trigger',
        hasPagination: !!this.pagination.next
      },
      this.loadConversions
    );
  }

  handleSort(field: string) {
    this.headers = PaginationUtils.getUpdatedHeaders(field, this.headers);

    const sortDir = this.headers[field].sortDir;

    this.filters = {
      ...this.filters,
      sort: `${field}:${sortDir}`
    };

    this.$analytics({
      eventAction: 'click',
      eventCategory: 'Conversions List - Sort',
      eventLabel: `[${sortDir}] ${this.headers[field].label} column`
    });

    this.loadConversions({ clearList: true });
  }

  handleCustomFilterChange(value: CustomSelectOption[]) {
    delete this.filters.status;

    const status = value[0].uuid;
    if (status) {
      this.filters = {
        ...this.filters,
        status
      };
    }
    this.loadConversions({ clearList: true });
  }

  handleSearchFilterChange(search: any) {
    delete this.filters.search;

    if (this.search.term) {
      this.filters = {
        ...this.filters,
        search: this.search.term
      };
    }

    this.loadConversions({ clearList: true });
  }

  handleFilterChange(initiatives: string[]) {
    this.filters = {
      ...this.filters,
      initiatives
    };

    this.loadConversions({ clearList: true });
  }
}
